/**
 * Mapping des enseignes normalisées vers leurs mots-clés de recherche.
 * Permet de trouver les rappels même avec des variations de noms.
 * Ex: "leclerc drumettaz", "e.leclerc morlaix" → trouvés via "Leclerc"
 */
export const RETAILER_KEYWORDS: Record<string, string[]> = {
  // Grande distribution - majeurs (avec toutes les variantes détectées)
  'Leclerc': [
    'leclerc', 'e.leclerc', 'e leclerc', 'e. leclerc', 'galec', 
    'centre e.leclerc', 'leclerc express', 'leclerc drive'
  ],
  'Carrefour': [
    'carrefour', 'carrefour market', 'carrefour express', 'carrefour city', 
    'carrefour contact', 'carrefour drive', 'carrefour bio', 'promocash'
  ],
  'Intermarché': [
    'intermarché', 'intermarche', 'intermarch', 'itm', 
    'inter marché', 'inter marche', 'intermarche super', 'intermarche hyper', 
    'intermarché contact', 'intermarché express', 'les mousquetaires'
  ],
  'Système U': [
    'système u', 'systeme u', 'super u', 'hyper u', 'u express', 
    'cooperative u', 'coopérative u', 'magasins u', 'u proximite', 
    'marché u', 'marche u', 'u', 'magasin u'
  ],
  'Auchan': [
    'auchan', 'auchan supermarché', 'auchan drive', 'simply market', 
    'my auchan', 'auchan retail'
  ],
  'Lidl': ['lidl', 'lidl france'],
  'Casino': [
    'casino', 'géant casino', 'geant casino', 'casino supermarché', 
    'petit casino', 'vival', 'spar', 'leader price'
  ],
  'Monoprix': ['monoprix', 'monop', 'monop\'', 'daily monop'],
  'Cora': ['cora', 'cora drive'],
  'Match': ['match', 'supermarchés match', 'supermarches match'],
  'Franprix': ['franprix', 'franprix noé'],
  'Aldi': ['aldi', 'aldi france'],
  'Netto': ['netto'],
  'Norma': ['norma'],
  'Colruyt': ['colruyt'],
  
  // Bio & Frais
  'Grand Frais': ['grand frais', 'grandfrais'],
  'Biocoop': ['biocoop', 'bio coop'],
  'Naturalia': ['naturalia'],
  'Picard': ['picard', 'picard surgelés'],
  'La Vie Claire': ['la vie claire', 'vie claire'],
  'Bio c\' Bon': ['bio c\' bon', 'bio c bon', 'biocbon'],
  
  // Commerces de proximité
  'Marchés locaux': [
    'marché', 'marchés', 'revendeurs marchés', 'vente directe', 'marche', 
    'marché local', 'marchés locaux', 'vente à la ferme', 'producteur'
  ],
  'Épiceries': [
    'epicerie', 'épicerie', 'épiceries', 'epiceries', 
    'épicerie fine', 'epicerie fine', 'traiteur'
  ],
  'Boucheries': [
    'boucherie', 'boucheries', 'charcuterie', 'charcuteries', 
    'boucherie charcuterie', 'artisan boucher'
  ],
  'Boulangeries': [
    'boulangerie', 'boulangeries', 'patisserie', 'pâtisserie', 
    'boulangerie pâtisserie', 'artisan boulanger'
  ],
  'Fromageries': [
    'fromagerie', 'fromager', 'fromagers', 'crèmerie', 'cremerie', 
    'fromages', 'affineur'
  ],
  'Primeurs': [
    'primeur', 'primeurs', 'fruits et légumes', 'fruits et legumes', 
    'légumes', 'fruits'
  ],
  
  // Santé
  'Pharmacies': [
    'pharmacie', 'pharmacies', 'parapharmacie', 'pharmacies et parapharmacies',
    'officine', 'officines'
  ],
  
  // Professionnels / Grossistes
  'Grossistes': [
    'grossiste', 'grossistes', 'répartiteur', 'repartiteur', 
    'grossistes répartiteurs', 'cash & carry', 'cash and carry',
    'gms', 'grande distribution', 'grande surface', 'moyenne surface'
  ],
  'Metro': ['metro', 'métro', 'metro france'],
  'Promocash': ['promocash', 'promo cash'],
  'Transgourmet': ['transgourmet'],
  'Pomona': ['pomona', 'france frais', 'fresh', 'passion froid'],
  
  // En ligne
  'Amazon': ['amazon', 'amazon fresh', 'amazon prime'],
  'Cdiscount': ['cdiscount'],
  'E-commerce': ['e-commerce', 'monmarche.fr', 'mon marché', 'vente en ligne'],
  
  // Enseignes spécialisées
  'Action': ['action'],
  'Stokomani': ['stokomani'],
  'Gifi': ['gifi'],
  'Centrakor': ['centrakor'],
  'La Foir\'Fouille': ['la foir\'fouille', 'foir fouille', 'foirfouille'],
};

/**
 * Catégories d'enseignes pour l'affichage dans les filtres
 */
export const RETAILER_CATEGORIES = [
  {
    id: 'grande-distribution',
    label: '🛒 Grande distribution',
    retailers: ['Leclerc', 'Carrefour', 'Intermarché', 'Système U', 'Auchan', 'Lidl', 'Casino', 'Monoprix', 'Cora', 'Match', 'Franprix', 'Aldi', 'Netto', 'Norma', 'Colruyt']
  },
  {
    id: 'bio-frais',
    label: '🥬 Bio & Frais',
    retailers: ['Grand Frais', 'Biocoop', 'Naturalia', 'Picard', 'La Vie Claire', 'Bio c\' Bon']
  },
  {
    id: 'proximite',
    label: '🏪 Commerces de proximité',
    retailers: ['Marchés locaux', 'Épiceries', 'Boucheries', 'Boulangeries', 'Fromageries', 'Primeurs']
  },
  {
    id: 'sante-pro',
    label: '💊 Santé & Pro',
    retailers: ['Pharmacies', 'Grossistes', 'Metro', 'Promocash', 'Transgourmet', 'Pomona']
  },
  {
    id: 'en-ligne',
    label: '📦 En ligne',
    retailers: ['Amazon', 'Cdiscount', 'E-commerce']
  },
  {
    id: 'specialisees',
    label: '🏬 Enseignes spécialisées',
    retailers: ['Action', 'Stokomani', 'Gifi', 'Centrakor', 'La Foir\'Fouille']
  }
];

/**
 * Récupère tous les mots-clés pour une liste d'enseignes sélectionnées
 */
export function getRetailerKeywords(selectedRetailers: string[]): string[] {
  const keywords: string[] = [];
  
  for (const retailer of selectedRetailers) {
    const retailerKeywords = RETAILER_KEYWORDS[retailer];
    if (retailerKeywords) {
      keywords.push(...retailerKeywords);
    } else {
      // Fallback: utiliser le nom de l'enseigne en minuscules
      keywords.push(retailer.toLowerCase());
    }
  }
  
  return keywords;
}
